import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box, Button, Chip, CircularProgress, IconButton,
  Tooltip, Typography,
} from '@mui/material'
import AddIcon          from '@mui/icons-material/Add'
import CheckCircleIcon  from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import EditIcon         from '@mui/icons-material/Edit'
import DeleteIcon       from '@mui/icons-material/Delete'
import AccessTimeIcon   from '@mui/icons-material/AccessTime'
import AssignmentIcon   from '@mui/icons-material/Assignment'
import { leadTasksApi, LeadTask, CreateLeadTaskPayload, UpdateLeadTaskPayload, TaskPriority, TaskStatus } from '../../lib/api/leadTasksApi'
import { leadsApi } from '../../lib/api/leadsApi'
import { authStore } from '../../lib/stores/authStore'
import { toast } from 'react-toastify'
import { themeColors } from '../../app/layout/theme'
import { TaskModal } from '../shared/TaskModal'

// ─── helpers ─────────────────────────────────────────────────────────────────

const PRIORITIES: { value: TaskPriority; label: string; color: string; bg: string }[] = [
  { value: 'LOW',    label: 'Low',    color: '#6b7280', bg: '#f3f4f6' },
  { value: 'MEDIUM', label: 'Medium', color: '#f59e0b', bg: '#fef3c7' },
  { value: 'HIGH',   label: 'High',   color: '#f97316', bg: '#fff7ed' },
  { value: 'URGENT', label: 'Urgent', color: '#dc2626', bg: '#fef2f2' },
]

const STATUSES: { value: TaskStatus; label: string; color: string; bg: string }[] = [
  { value: 'PENDING',     label: 'Pending',     color: '#6b7280', bg: '#f3f4f6' },
  { value: 'IN_PROGRESS', label: 'In Progress', color: '#0070d2', bg: '#e8f4fd' },
  { value: 'DONE',        label: 'Done',        color: '#16a34a', bg: '#f0fdf4' },
  { value: 'CANCELLED',   label: 'Cancelled',   color: '#9ca3af', bg: '#f9fafb' },
]

function getPriority(p: TaskPriority) {
  return PRIORITIES.find(x => x.value === p) ?? PRIORITIES[1]
}

function getStatus(s: TaskStatus) {
  return STATUSES.find(x => x.value === s) ?? STATUSES[0]
}

function isDueToday(task: LeadTask) {
  if (!task.dueDate || task.status === 'DONE' || task.status === 'CANCELLED') return false
  const due = new Date(task.dueDate)
  const today = new Date()
  return due.getFullYear() === today.getFullYear() &&
         due.getMonth() === today.getMonth() &&
         due.getDate() === today.getDate()
}

function isOverdue(task: LeadTask) {
  if (!task.dueDate || task.status === 'DONE' || task.status === 'CANCELLED') return false
  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  return new Date(task.dueDate) < startOfToday
}

function fmtDue(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}


// ─── Single Task Card ─────────────────────────────────────────────────────────

interface TaskCardProps {
  task:       LeadTask
  onEdit:     (t: LeadTask) => void
  onToggle:   (t: LeadTask) => void
  onDelete:   (t: LeadTask) => void
  isMutating: boolean
}

function TaskCard({ task, onEdit, onToggle, onDelete, isMutating }: TaskCardProps) {
  const priority = getPriority(task.priority)
  const status   = getStatus(task.status)
  const done     = task.status === 'DONE'
  const cancelled = task.status === 'CANCELLED'
  const overdue   = isOverdue(task)
  const dueToday  = isDueToday(task)

  return (
    <Box sx={{
      display: 'flex', gap: 1.5, alignItems: 'flex-start',
      p: 1.5, borderRadius: '10px', mb: 1.5,
      border: overdue ? '1px solid #fecaca' : '1px solid #e5e7eb',
      bgcolor: done || cancelled ? '#fafafa' : overdue ? '#fff5f5' : '#fff',
      opacity: cancelled ? 0.65 : 1,
      transition: 'box-shadow 0.15s',
      '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.07)' },
    }}>
      {/* Done toggle */}
      <Tooltip title={done ? 'Mark Pending' : 'Mark Done'}>
        <span>
          <IconButton size="small" disabled={isMutating || cancelled}
            onClick={() => onToggle(task)}
            sx={{ color: done ? '#16a34a' : '#d1d5db', p: 0.5,
              '&:hover': { color: done ? '#15803d' : '#16a34a' } }}>
            {done
              ? <CheckCircleIcon sx={{ fontSize: 20 }} />
              : <RadioButtonUncheckedIcon sx={{ fontSize: 20 }} />
            }
          </IconButton>
        </span>
      </Tooltip>

      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{
            fontWeight: 600, fontSize: 14,
            color: done || cancelled ? '#9ca3af' : '#111827',
            textDecoration: done ? 'line-through' : 'none',
          }}>
            {task.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, alignItems: 'center' }}>
            <Chip label={priority.label} size="small" sx={{
              fontWeight: 600, fontSize: 11, height: 20,
              bgcolor: priority.bg, color: priority.color,
            }} />
            <Chip label={status.label} size="small" sx={{
              fontWeight: 600, fontSize: 11, height: 20,
              bgcolor: status.bg, color: status.color,
            }} />
            <Tooltip title="Edit">
              <span>
                <IconButton size="small" disabled={isMutating || cancelled} onClick={() => onEdit(task)}
                  sx={{ color: '#9ca3af', '&:hover': { color: '#374151' } }}>
                  <EditIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <span>
                <IconButton size="small" disabled={isMutating || cancelled} onClick={() => onDelete(task)}
                  sx={{ color: '#dc2626', '&:hover': { color: '#b91c1c' } }}>
                  <DeleteIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {task.description && (
          <Typography sx={{ fontSize: 13, color: '#6b7280', mt: 0.3, lineHeight: 1.4 }}>
            {task.description}
          </Typography>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.75, flexWrap: 'wrap' }}>
          {task.dueDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <AccessTimeIcon sx={{ fontSize: 12, color: overdue ? '#dc2626' : dueToday ? '#f59e0b' : '#9ca3af' }} />
              <Typography sx={{
                fontSize: 12,
                color: overdue ? '#dc2626' : dueToday ? '#f59e0b' : '#9ca3af',
                fontWeight: overdue || dueToday ? 600 : 400,
              }}>
                {overdue ? 'Overdue · ' : dueToday ? 'Due today · ' : 'Due '}{fmtDue(task.dueDate)}
              </Typography>
            </Box>
          )}
          {task.assignedTo && (
            <Typography sx={{ fontSize: 12, color: '#9ca3af' }}>
              → <strong style={{ color: '#6b7280' }}>{task.assignedTo.name}</strong>
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

interface Props { leadId: string; canModify: boolean }

const LeadTasksSection = observer(function LeadTasksSection({ leadId, canModify }: Props) {
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editTask,  setEditTask]  = useState<LeadTask | null>(null)

  const { data: tasks = [], isLoading } = useQuery({
    queryKey: ['lead-tasks', leadId],
    queryFn:  () => leadTasksApi.getAll(leadId),
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['lead-tasks', leadId] })
    qc.invalidateQueries({ queryKey: ['calendar-lead-tasks'] })
    qc.invalidateQueries({ queryKey: ['tasks', 'kanban'] })
  }

  const createMut = useMutation({
    mutationFn: (p: CreateLeadTaskPayload) => leadTasksApi.create(leadId, p),
    onSuccess: () => {
      invalidate()
      qc.invalidateQueries({ queryKey: ['lead-activities', leadId] })
      setModalOpen(false)
    },
  })

  const updateMut = useMutation({
    mutationFn: ({ taskId, payload }: { taskId: string; payload: UpdateLeadTaskPayload }) =>
      leadTasksApi.update(leadId, taskId, payload),
    onSuccess: async (res, variables) => {
      const task = tasks.find(t => t.id === variables.taskId)
      const userId = authStore.user?.userId
      if (task && userId && variables.payload.status) {
        let action = 'updated'
        if (variables.payload.status === 'DONE') action = 'completed'
        else if (variables.payload.status === 'PENDING') action = 'reopened'
        try {
          await leadsApi.addActivity(leadId, {
            type: 'TASK',
            description: `Task ${action}: ${task.title}`,
            performedById: userId,
            metadataJson: null,
          })
        } catch (err) {
          // ignore
        }
      }
      invalidate()
      qc.invalidateQueries({ queryKey: ['lead-activities', leadId] })
      setEditTask(null)
    },
  })

  const deleteMut = useMutation({
    mutationFn: (task: LeadTask) => leadTasksApi.update(leadId, task.id, { status: 'CANCELLED' }),
    onSuccess: async (res, task) => {
      const userId = authStore.user?.userId
      if (userId) {
        try {
          await leadsApi.addActivity(leadId, {
            type: 'TASK',
            description: `Task deleted: ${task.title}`,
            performedById: userId,
            metadataJson: null,
          })
        } catch (err) {
          // ignore
        }
      }
      invalidate()
      toast.success('Task deleted')
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message ?? 'Failed to delete task')
    }
  })

  const handleDelete = (task: LeadTask) => {
    deleteMut.mutate(task)
  }

  function handleToggle(task: LeadTask) {
    const newStatus: TaskStatus = task.status === 'DONE' ? 'PENDING' : 'DONE'
    updateMut.mutate({ taskId: task.id, payload: { status: newStatus } })
  }

  const pending    = tasks.filter(t => t.status === 'PENDING' || t.status === 'IN_PROGRESS')
  const completed  = tasks.filter(t => t.status === 'DONE' || t.status === 'CANCELLED')
  const isMutating = createMut.isPending || updateMut.isPending || deleteMut.isPending

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  return (
    <Box>
      {/* Toolbar */}
      {canModify && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="contained" size="small" startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
            sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600,
              background: themeColors.gradient?.blue ?? themeColors.primary, fontSize: 13 }}>
            Add Task
          </Button>
        </Box>
      )}

      {/* Empty state */}
      {tasks.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <AssignmentIcon sx={{ fontSize: 48, color: '#e5e7eb', mb: 1 }} />
          <Typography sx={{ color: '#9ca3af', fontWeight: 500 }}>No tasks yet</Typography>
          <Typography sx={{ color: '#d1d5db', fontSize: 13 }}>Add action items to keep track of what needs to be done</Typography>
        </Box>
      )}

      {/* Pending / active */}
      {pending.length > 0 && (
        <Box>
          {pending.map(t => (
            <TaskCard key={t.id} task={t} onEdit={setEditTask} onToggle={handleToggle} onDelete={handleDelete} isMutating={isMutating} />
          ))}
        </Box>
      )}

      {/* Completed section */}
      {completed.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase',
            letterSpacing: 1, mb: 1 }}>
            Completed ({completed.length})
          </Typography>
          {completed.map(t => (
            <TaskCard key={t.id} task={t} onEdit={setEditTask} onToggle={handleToggle} onDelete={handleDelete} isMutating={isMutating} />
          ))}
        </Box>
      )}

      {/* Modals */}
      <TaskModal
        open={modalOpen}
        entityType="lead"
        onClose={() => setModalOpen(false)}
        onSave={p => createMut.mutate(p as CreateLeadTaskPayload)}
        isSaving={createMut.isPending}
      />
      <TaskModal
        open={editTask !== null}
        task={editTask}
        entityType="lead"
        onClose={() => setEditTask(null)}
        onSave={p => editTask && updateMut.mutate({ taskId: editTask.id, payload: p as UpdateLeadTaskPayload })}
        isSaving={updateMut.isPending}
      />
    </Box>
  )
})

export default LeadTasksSection
